import {
  LocationLaneDetail,
  type AutoChargeVisitConfig,
  type LocationLane,
  type StaffingConfig,
} from '@m/types/api';
import { apiFetchV2 } from '@m/utils/http';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { getLaneDetailQueryKey, getSiteLanesQueryKey } from './query-keys';
import { adminSiteLane, adminSiteLanes } from './routes';

type SiteLanesResponse = {
  lanes: LocationLane[];
};

export type UpdateLaneBody = {
  autoChargeVisitConfig?: AutoChargeVisitConfig;
  name: string;
  staffingConfig?: StaffingConfig;
};

export type UpdateLaneVariables = {
  body: UpdateLaneBody;
  laneId: number;
  locationId: number;
};

const getSiteLanes = (siteId: string) =>
  apiFetchV2<SiteLanesResponse>(adminSiteLanes(siteId)).then((data) => data?.lanes ?? []);

const updateLane = ({ locationId, laneId, body }: UpdateLaneVariables) =>
  apiFetchV2<LocationLane>(adminSiteLane(locationId, laneId), {
    method: 'POST',
    body,
  });

const getLaneDetail = ({ locationId, laneId }: { laneId: number; locationId: number }) =>
  apiFetchV2<LocationLaneDetail>(adminSiteLane(locationId, laneId));

export const useSiteLanes = (siteId: string) =>
  useQuery({
    queryKey: getSiteLanesQueryKey(siteId),
    queryFn: () => getSiteLanes(siteId),
    enabled: !!siteId,
  });

export const invalidateSiteLanesQuery = (queryClient: QueryClient, siteId: string) =>
  queryClient.invalidateQueries({
    queryKey: getSiteLanesQueryKey(siteId),
  });

export const useInvalidateSiteLanesQuery = () => {
  const queryClient = useQueryClient();

  return (siteId: string) => {
    invalidateSiteLanesQuery(queryClient, siteId);
  };
};

export const useLaneDetail = (locationId: number, laneId: number) =>
  useQuery({
    queryKey: getLaneDetailQueryKey(locationId, laneId),
    queryFn: () => getLaneDetail({ locationId, laneId }),
    enabled: !!locationId && !!laneId,
  });

export function useUpdateLane() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateLane,
    onMutate: async (variables) => {
      const siteId = String(variables.locationId);
      const laneDetailKey = getLaneDetailQueryKey(variables.locationId, variables.laneId);
      const siteLanesKey = getSiteLanesQueryKey(siteId);

      await queryClient.cancelQueries({ queryKey: laneDetailKey });
      await queryClient.cancelQueries({ queryKey: siteLanesKey });

      const previousLaneDetail = queryClient.getQueryData(laneDetailKey);
      const previousSiteLanes = queryClient.getQueryData<LocationLane[]>(siteLanesKey);

      queryClient.setQueryData<LocationLaneDetail | undefined>(laneDetailKey, (old) => {
        if (!old?.laneDetails) return old;
        return {
          ...old,
          laneDetails: { ...old.laneDetails, ...variables.body },
        };
      });

      queryClient.setQueryData<LocationLane[] | undefined>(siteLanesKey, (old) => {
        if (!old) return old;
        return old.map((lane) =>
          lane.id === variables.laneId ? { ...lane, ...variables.body } : lane,
        );
      });

      return { previousLaneDetail, previousSiteLanes, laneDetailKey, siteLanesKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousLaneDetail !== undefined) {
        queryClient.setQueryData(context.laneDetailKey, context.previousLaneDetail);
      }
      if (context?.previousSiteLanes !== undefined) {
        queryClient.setQueryData(context.siteLanesKey, context.previousSiteLanes);
      }
    },
    onSettled: (_data, _error, variables, context) => {
      if (context?.laneDetailKey) {
        queryClient.invalidateQueries({ queryKey: context.laneDetailKey });
      }
      if (context?.siteLanesKey) {
        invalidateSiteLanesQuery(queryClient, variables.locationId.toString());
      }
    },
  });
}
